import { Button, Flex, Space, Table } from 'antd';
import {
  useCreateSalaryAdjustment,
  useDeleteSalaryAdjustment,
  useEmployeeSalaryAdjustments,
  useSalaryAdjustmentFilters,
  useUpdateSalaryAdjustment,
} from '../../utils/api-hooks/employee.hook';
import moment from 'moment';
import { usePagination } from '../../shared/usePagination';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import useModal from 'antd/es/modal/useModal';
import EmployeeSalaryAdjustmentFormComponent from './employee-salary-adjustment.form.component';
import { useRef, useState } from 'react';
import { FormInstance } from 'antd/lib';
import {
  CreateEmployeeSalaryAdjustmentDto,
  GetAllEmployeeSalaryAdjustmentsResult,
} from '../../utils/api-client';
import {
  EMPLOYEE_SALARY_ADJUSTMENT_STATUS,
  EMPLOYEE_SALARY_ADJUSTMENT_STATUS_LABEL,
} from '../../constants/employee';
import dayjs, { Dayjs } from 'dayjs';
import { map } from 'lodash';
import { EmployeeSalaryAdjustmentsImportForm } from './employee-salary-adjustments-import-form';

export default function EmployeeSalaryAdjustmentsContainer() {
  const [modal, contextHolder] = useModal();
  const [filters, setFilters] = useState<{
    status?: (keyof typeof EMPLOYEE_SALARY_ADJUSTMENT_STATUS)[];
    employeeId?: string[];
    time?: string[];
  }>({});
  const createForm = useRef<
    FormInstance<
      CreateEmployeeSalaryAdjustmentDto & {
        adjustmentType: 'ADDITION' | 'DEDUCTION';
        time: Dayjs;
      }
    >
  >(null);
  const pagination = usePagination();
  const {
    data: { date: dateFilters = [], name: nameFilters = [] } = {},
    isLoading: filtersLoading,
  } = useSalaryAdjustmentFilters();
  const {
    mutateAsync: createEmployeeSalaryAdjustment,
    isLoading: isCreatingEmployeeSalaryAdjustment,
  } = useCreateSalaryAdjustment();
  const {
    mutateAsync: deleteEmployeeSalaryAdjustment,
    isLoading: isDeletingEmployeeSalaryAdjustment,
  } = useDeleteSalaryAdjustment();
  const {
    mutateAsync: updateEmployeeSalaryAdjustment,
    isLoading: isUpdatingEmployeeSalaryAdjustment,
  } = useUpdateSalaryAdjustment();
  const {
    data: { data: employeeSalaryAdjustments, total } = {},
    isLoading: employeeSalaryAdjustmentsLoading,
    isFetching: employeeSalaryAdjustmentsFetching,
  } = useEmployeeSalaryAdjustments(
    filters.employeeId,
    filters.status,
    undefined,
    filters.time,
    undefined,
    undefined,
    pagination.take,
    pagination.skip
  );

  const isLoading =
    employeeSalaryAdjustmentsLoading ||
    employeeSalaryAdjustmentsFetching ||
    isCreatingEmployeeSalaryAdjustment ||
    isDeletingEmployeeSalaryAdjustment ||
    isUpdatingEmployeeSalaryAdjustment ||
    filtersLoading;

  const onImportButtonClick = () => {
    modal.info({
      title: '匯入薪資調整項目',
      content: <EmployeeSalaryAdjustmentsImportForm />,
      icon: null,
      okText: '取消',
    });
  };

  const onCreateButtonCLick = async () => {
    modal.confirm({
      title: '新增薪資調整項目',
      content: <EmployeeSalaryAdjustmentFormComponent formRef={createForm} />,
      icon: null,
      async onOk() {
        const values = await createForm.current?.validateFields()!;

        return createEmployeeSalaryAdjustment({
          ...values,
          amount:
            values.adjustmentType === 'ADDITION'
              ? values.amount
              : -values.amount,
          time: values.time.toISOString(),
        });
      },
    });
  };

  const onDeleteButtonClick = async (
    record: GetAllEmployeeSalaryAdjustmentsResult['data'][number]
  ) => {
    modal.confirm({
      title: '刪除薪資調整項目',
      content: '確定要刪除這筆薪資調整項目嗎？',
      async onOk() {
        return deleteEmployeeSalaryAdjustment(record.id);
      },
    });
  };

  const onEditButtonClick = async (
    record: GetAllEmployeeSalaryAdjustmentsResult['data'][number]
  ) => {
    modal.confirm({
      title: '編輯薪資調整項目',
      content: (
        <EmployeeSalaryAdjustmentFormComponent
          formRef={createForm}
          initialValues={{
            ...record,
            adjustmentType: record.amount > 0 ? 'ADDITION' : 'DEDUCTION',
            employeeId: record.employee.id,
            note: record.note || undefined,
            time: dayjs(record.time),
          }}
        />
      ),
      icon: null,
      async onOk() {
        const values = await createForm.current?.validateFields()!;

        return updateEmployeeSalaryAdjustment({
          id: record.id,
          ...values,
          amount:
            values.adjustmentType === 'ADDITION'
              ? values.amount
              : -values.amount,
          time: values.time.toISOString(),
        });
      },
    });
  };

  return (
    <Space direction="vertical" className="w-full">
      {contextHolder}
      <span className="text-2xl font-bold">員工薪資調整項目</span>
      <Table
        dataSource={employeeSalaryAdjustments}
        loading={isLoading}
        title={() => (
          <Flex gap={8}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              loading={isCreatingEmployeeSalaryAdjustment}
              onClick={onCreateButtonCLick}
            >
              新增薪資調整項目
            </Button>
            <Button onClick={onImportButtonClick}>匯入薪資調整項目</Button>
          </Flex>
        )}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        size="small"
        onChange={(_pagination, filters) => {
          setFilters({
            status: filters.status as
              | (keyof typeof EMPLOYEE_SALARY_ADJUSTMENT_STATUS)[]
              | undefined,
            employeeId: [
              ...((filters['employee.idNumber'] || []) as string[]),
              ...((filters['employee.displayName'] || []) as string[]),
            ] as string[] | undefined,
            time: filters.time as string[] | undefined,
          });
        }}
        pagination={{
          ...pagination,
          total,
        }}
        columns={[
          {
            align: 'center',
            title: '狀態',
            width: 80,
            dataIndex: 'status',
            filters: map(
              EMPLOYEE_SALARY_ADJUSTMENT_STATUS_LABEL,
              (value, key) => ({
                text: value,
                value: key,
              })
            ),
            render(value: keyof typeof EMPLOYEE_SALARY_ADJUSTMENT_STATUS) {
              return (
                <span
                  className={
                    value === 'PENDING' ? 'text-red-500' : 'text-green-500'
                  }
                >
                  {EMPLOYEE_SALARY_ADJUSTMENT_STATUS_LABEL[value]}
                </span>
              );
            },
          },
          {
            title: '日期',
            dataIndex: 'time',
            width: 120,
            filterSearch: true,
            filters: map(dateFilters, (value) => ({
              text: value.text,
              value: value.value,
            })),
            render(value) {
              return moment(value).format('YYYY/MM/DD');
            },
          },
          {
            title: '姓名',
            dataIndex: ['employee', 'displayName'],
            filterSearch: true,
            filters: map(nameFilters, (value) => ({
              text: value.text,
              value: value.value,
            })),
            width: 100,
            render(value) {
              return value || '--';
            },
          },
          {
            title: '名目',
            width: 150,
            dataIndex: 'type',
          },
          {
            title: '金額',
            align: 'center',
            width: 100,
            dataIndex: 'amount',
            render(value) {
              return (
                <span className={value < 0 ? 'text-red-500' : ''}>
                  {value.toLocaleString('zh-TW', {
                    style: 'currency',
                    currency: 'TWD',
                    minimumFractionDigits: 0,
                  })}
                </span>
              );
            },
          },
          {
            title: '備註',
            dataIndex: 'note',
          },
          {
            title: '操作',
            width: 80,
            dataIndex: 'action',
            render: (_, record) => (
              <Space>
                <Button
                  shape="circle"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => onEditButtonClick(record)}
                />
                <Button
                  shape="circle"
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                  onClick={() => onDeleteButtonClick(record)}
                />
              </Space>
            ),
          },
        ]}
      />
    </Space>
  );
}
